import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, Clock, Copy, Linkedin, Check, ArrowLeft } from 'lucide-react';
import { Reveal } from '../components/Shared';
import { publicApi, mediaUrl, formatPostDate } from '../lib/publicApi';
import { useSeo } from '../hooks/useSeo';

const withHeadingIds = html => {
  if (!html) return { html: '', toc: [] };
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const toc = [];
  doc.querySelectorAll('h2, h3').forEach((node, i) => {
    const id = `section-${i + 1}`;
    node.setAttribute('id', id);
    toc.push({ id, text: node.textContent, level: node.tagName === 'H3' ? 3 : 2 });
  });
  doc.querySelectorAll('img').forEach(img => img.setAttribute('loading', 'lazy'));
  return { html: doc.body.firstChild?.innerHTML || html, toc };
};

export default function BlogPost() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, post: null, related: [], missing: false });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setState({ loading: true, post: null, related: [], missing: false });
    publicApi.post(slug)
      .then(data => { if (active) setState({ loading: false, post: data.post, related: data.related, missing: false }); })
      .catch(err => { if (active) setState({ loading: false, post: null, related: [], missing: err.message === 'not-found' }); });
    return () => { active = false; };
  }, [slug]);

  const { post, related, loading, missing } = state;
  const { html, toc } = useMemo(() => withHeadingIds(post?.body), [post]);

  useSeo(useMemo(() => (post ? {
    title: post.seo_title || `${post.title} — TechSpitze`,
    description: post.seo_description || post.excerpt,
    image: mediaUrl(post.cover_image),
    jsonLd: {
      '@context': 'https://schema.org', '@type': 'Article', headline: post.title,
      description: post.seo_description || post.excerpt, image: mediaUrl(post.cover_image),
      datePublished: post.publish_date, dateModified: post.updated_at,
      author: { '@type': 'Person', name: post.author?.name || 'TechSpitze' },
      publisher: { '@type': 'Organization', name: 'TechSpitze' },
      articleSection: post.category, keywords: (post.tags || []).join(', '),
    },
  } : {}), [post]));

  const share = network => {
    const url = window.location.href;
    if (network === 'copy') { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); return; }
    const target = network === 'linkedin'
      ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
      : `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <main id="main-content" className="article-page container" data-testid="article-loading"><div className="article-skeleton" /><div className="article-skeleton short" /></main>;
  if (missing || !post) return <main id="main-content" className="container not-found" data-testid="article-not-found">
    <span className="eyebrow">404 / ARTICLE NOT FOUND</span>
    <h1>That article<br /><span className="heading-accent">has moved on.</span></h1>
    <Link className="action-button button-primary" to="/blog" data-testid="article-back-to-blog">Back to Insights ↗</Link>
  </main>;

  return <main id="main-content" className="article-page" data-testid="article-page">
    <div className="container article-top">
      <Link to="/blog" className="article-back" data-testid="article-back"><ArrowLeft size={15} />All insights</Link>
      <span className="article-category" data-testid="article-category">{post.category}</span>
      <h1 data-testid="article-title">{post.title}</h1>
      <div className="article-byline" data-testid="article-byline">
        <span className="article-avatar" aria-hidden="true">{(post.author?.name || 'T').charAt(0)}</span>
        <div>
          <strong>{post.author?.name || 'TechSpitze'}</strong>
          <small>{post.author?.role || 'TechSpitze Team'}</small>
        </div>
        <span className="article-byline-meta">{formatPostDate(post.publish_date)} · <Clock size={12} />{post.read_time}</span>
      </div>
    </div>

    {post.cover_image && <div className="container article-cover" data-testid="article-cover"><img src={mediaUrl(post.cover_image)} alt={post.title} loading="lazy" /></div>}

    <div className="container article-layout">
      <aside className="article-toc" aria-label="Table of contents" data-testid="article-toc">
        <span className="article-toc-title">On this page</span>
        <nav>{toc.map(item => <a key={item.id} href={`#${item.id}`} className={item.level === 3 ? 'is-sub' : ''} data-testid={`article-toc-${item.id}`}>{item.text}</a>)}</nav>
        <div className="article-share" data-testid="article-share">
          <span className="article-toc-title">Share</span>
          <div>
            <button type="button" onClick={() => share('linkedin')} aria-label="Share on LinkedIn" data-testid="article-share-linkedin"><Linkedin size={15} /></button>
            <button type="button" onClick={() => share('x')} aria-label="Share on X" data-testid="article-share-x"><span aria-hidden="true">X</span></button>
            <button type="button" onClick={() => share('copy')} aria-label="Copy link" data-testid="article-share-copy">{copied ? <Check size={15} /> : <Copy size={15} />}</button>
          </div>
        </div>
      </aside>

      <article className="article-body rich-text" data-testid="article-body" dangerouslySetInnerHTML={{ __html: html }} />
    </div>

    {related.length > 0 && <section className="container article-related" data-testid="article-related">
      <h2>Related reading</h2>
      <div className="post-grid">{related.map(item => <Reveal key={item.slug}>
        <Link to={`/blog/${item.slug}`} className="post-card" data-testid={`related-${item.slug}`}>
          <div className="post-card-cover">{item.cover_image ? <img src={mediaUrl(item.cover_image)} alt={item.title} loading="lazy" /> : <span className="post-card-cover-empty" />}<span className="post-card-tag">{item.category}</span></div>
          <div className="post-card-body"><h3>{item.title}</h3><p className="post-card-excerpt">{item.excerpt}</p></div>
        </Link>
      </Reveal>)}</div>
    </section>}

    <section className="container content-cta" data-testid="article-cta">
      <div className="content-cta-band glass">
        <div><span className="eyebrow"><span className="eyebrow-line" />PUT IT TO WORK</span><h2>Ready to map this to your business?</h2></div>
        <Link to="/intake-form" className="action-button button-primary" data-testid="article-cta-button">Start the intake form <ArrowUpRight size={17} /></Link>
      </div>
    </section>
  </main>;
}
